import ButtonCircle3 from "@/shared/Button/ButtonCircle3";
import Logo from "@/shared/Logo/Logo";
import { Link } from "react-router-dom";
import CartSideBar from "../CartSideBar";
import { useEffect, useRef, useState } from "react";

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: {
      REACT_APP_BASE_URL?: string;
      REACT_APP_SELF_URL?: string;
      REACT_APP_PORTAL_URL?: string;
      REACT_APP_KEY_ID?: string;
      REACT_APP_ORDER_CURRENCY?: string;
      REACT_APP_ORDER_AMOUNT?: string;
      REACT_APP_PRIVATE_KEY?: string;
      REACT_APP_PUBLIC_KEY?: string;
      REACT_APP_ACCESS_TOKEN?: string;
      REACT_APP_REFRESH_TOKEN?: string;
      REACT_APP_FLAVOR?: string;
    };
    PayrollSDK?: {
      init: (config: {
        accessToken: string | undefined;
        refreshToken: string | undefined;
        flavor: string | undefined;
        onSuccess?: (data: {
          transactionId: string;
          amount: number;
          currency: string;
          method: string;
        }) => void;
        onError?: (error: string) => void;
        onSessionExpired?: () => void;
      }) => void;
      open: () => void;
      close: () => void;
    };
  }
}

const MainNav = () => {
  const [aToken, setAToken] = useState<string | undefined>(undefined);
  const [rToken, setRToken] = useState<string | undefined>(undefined);
  // FIX: use ref so initAndOpen() always reads the latest script instance
  // without causing re-renders
  const scriptLoadedRef = useRef(false);

  const portalUrl =
    window.__RUNTIME_CONFIG__?.REACT_APP_PORTAL_URL ||
    (window.location.hostname.includes('sandbox')
      ? 'https://sandbox.portal.relm.co'
      : 'http://localhost:3000');

  useEffect(() => {
    if (!aToken || !rToken) return;

    const initAndOpen = () => {
      if (!window.PayrollSDK) return;
      window.PayrollSDK.init({
        accessToken: aToken,
        refreshToken: rToken,
        flavor: window.__RUNTIME_CONFIG__?.REACT_APP_FLAVOR || "LOCAL",
        onSuccess: (data) => console.log("Payout success:", data),
        onError: (err) => console.error("Payout error:", err),
        onSessionExpired: () => console.log("session expired"),
      });
      window.PayrollSDK.open();
    };

    // FIX: check ref instead of querying the DOM on every render
    if (scriptLoadedRef.current) {
      initAndOpen();
      return;
    }

    const existing = document.querySelector(
      `script[src="${portalUrl}/sdk/payroll-sdk.js"]`,
    );

    if (existing) {
      scriptLoadedRef.current = true;
      initAndOpen();
      return;
    }

    const script = document.createElement("script");
    script.src = `${portalUrl}/sdk/payroll-sdk.js`;
    script.async = true;
    script.onload = () => {
      scriptLoadedRef.current = true;
      initAndOpen();
    };
    document.body.appendChild(script);
  }, [aToken, rToken]);

  const handlePayrollSDK = async (): Promise<undefined> => {
    const path = "/v1/api-keys/tokens";

    const keyID =
      window.__RUNTIME_CONFIG__?.REACT_APP_KEY_ID ?? "dc59e38bae904894";
    const baseUrl =
      window.__RUNTIME_CONFIG__?.REACT_APP_BASE_URL ??
      "https://sandbox.api.relm.co";
    const privateKey =
      window.__RUNTIME_CONFIG__?.REACT_APP_PRIVATE_KEY ??
      "MIGEAgEAMBAGByqGSM49AgEGBSuBBAAKBG0wawIBAQQguLLMKWdeTgETEAXsKJrcY5ShepEKrlae6r61jf+qHEKhRANCAARdvRrQ5koFp3c/fAxcTnZLRm7cmeiGtHNXrJ7lVm/k7B4o1MHgQalmdBpqGEbBt0kjqbfrOcplKhWWsfDAitWw";
    const publicKey =
      window.__RUNTIME_CONFIG__?.REACT_APP_PUBLIC_KEY ??
      "MFYwEAYHKoZIzj0CAQYFK4EEAAoDQgAEXb0a0OZKBad3P3wMXE52S0Zu3JnohrRzV6ye5VZv5OweKNTB4EGpZnQaahhGwbdJI6m36znKZSoVlrHwwIrVsA==";

    const signRes = await fetch(`${baseUrl}/v1/api-keys/${keyID}/signature`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        privateKey,
        publicKey,
        method: "GET",
        path,
        contentType: "application/json",
      }),
    });

    if (!signRes.ok)
      throw new Error(`Signature generation failed: ${signRes.status}`);

    const signResult = await signRes.json();
    const signature = signResult?.data?.signature;
    if (!signature)
      throw new Error("No signature returned from signing service");

    const response = await fetch(`${baseUrl}/v1/api-keys/tokens`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: signature,
      },
    });

    if (!response.ok) throw new Error(`Token fetch failed: ${response.status}`);

    const data = await response.json();
    const { token, refreshToken } = data.data;
    setAToken(token);
    setRToken(refreshToken);
  };

  return (
    <div className="container flex items-center justify-between py-4">
      <div className="flex items-center gap-5 lg:basis-3/5">
        <Logo />
      </div>

      <div className="flex flex-1 items-center justify-end gap-5">
        <div className="flex items-center divide-x divide-neutral-300">
          <div className="pr-5" onClick={handlePayrollSDK}>
            <div className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-blue-700 transition cursor-pointer">
              Payroll
            </div>
          </div>
          <CartSideBar />
          <div className="flex items-center gap-2 pl-5">
            <ButtonCircle3
              className="overflow-hidden bg-gray"
              size="w-10 h-10"
            ></ButtonCircle3>
            <Link to="/signup" className="hidden text-sm lg:block">
              Clark Kent
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainNav;
